import {
  Button,
  Center,
  Group,
  LoadingOverlay,
  Pagination,
  Stack,
  Tabs,
  Text,
} from "@mantine/core";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EventsList, EventsSearchFilter } from "components/events";
import {
  useDeleteEvent,
  useDeleteEvents,
  useQueryEvents,
  useQueryEventsCount,
} from "hooks/events";
import { useState, useEffect } from "react";
import { PublishedState } from "types";
import { usePreDownload } from "hooks/usePreDownload";
import { useQueryOrganizers } from "hooks/organizers";
import { YesNoDialog } from "components/YesNoDialog";
import { useQueryCurrentUser } from "hooks/users";
import { useAppTranslation } from "../../hooks/useTranslation";
import i18n from "../../i18n/i18n";

const perPage = 20;

// Fallback translation function in case the hook fails
const fallbackTranslation = (key: string): string => {
  try {
    return i18n.t(key).toString();
  } catch (e) {
    console.error("Translation error:", e);
    return key.split(".").pop() || key; // Return last part of the key as fallback
  }
};

export const Events = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [batchAction] = useState<string | null>(null);
  const [approveBatch, setApproveBatch] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchEvents, setBatchEvents] = useState<number[]>([]);

  // State to track if translations are ready
  const [translationsReady, setTranslationsReady] = useState(
    i18n.isInitialized
  );

  // Always call the hook, but handle potential errors
  const { t: translationFn } = useAppTranslation();
  // Create a safe translation function
  const t = (key: string): string => {
    try {
      return translationFn(key);
    } catch (e) {
      console.error("Translation error:", e);
      return fallbackTranslation(key);
    }
  };

  // Make sure i18n is initialized
  useEffect(() => {
    if (!i18n.isInitialized) {
      const checkInitialization = setInterval(() => {
        if (i18n.isInitialized) {
          setTranslationsReady(true);
          clearInterval(checkInitialization);
        }
      }, 500);

      // Clean up interval
      return () => clearInterval(checkInitialization);
    }
  }, []);

  const page = parseInt(searchParams.get("page") || "1", 10);

  const [title, setTitle] = useState(searchParams.get("title") || "");
  const [type, setType] = useState<number | undefined>(
    searchParams.get("type") !== null
      ? parseInt(searchParams.get("type")!, 10)
      : undefined
  );
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    searchParams.get("start_date") !== null &&
      searchParams.get("end_date") !== null
      ? [
          new Date(searchParams.get("start_date")!),
          new Date(searchParams.get("end_date")!),
        ]
      : [null, null]
  );

  const [organizer, setOrganizer] = useState<string | undefined>(
    searchParams.get("organizer") || undefined
  );
  const [organizerEvents, setOrganizerEvents] = useState<boolean>(
    searchParams.get("organizer_events") === "true"
  );
  const [activeTab, setActiveTab] = useState<PublishedState>(
    (searchParams.get("published_state") as PublishedState) || "published"
  );

  const deleteEvent = useDeleteEvent(() => navigate("/events"));
  const deleteEvents = useDeleteEvents(() => {
    setBatchEvents([]);
  });

  const {
    isLoading: isLoadingUsers,
    isError: isErrorUsers,
    error: errorUsers,
    organizers,
  } = useQueryOrganizers({});

  const {
    isLoading,
    isError,
    error,
    events,
    publishedEventsCount,
    draftEventsCount,
    archievedEventsCount,
    currentUser,
  } = usePreDownload([
    {
      hookName: "events",
      hook: useQueryEvents({
        title,
        startDate: dateRange[0],
        endDate: dateRange[1],
        page,
        perPage,
        publishedState: activeTab,
        isMunicipality:
          organizer === "Municipalities"
            ? true
            : organizer === "Organizers"
            ? false
            : undefined,
        userUid:
          organizer !== "Municipalities" && organizer !== "users"
            ? organizers?.find((user: any) => user.name === organizer)?.uid
            : undefined,
        madeByOrganizer: organizerEvents || undefined,
      }),
    },
    {
      hookName: "publishedEventsCount",
      hook: useQueryEventsCount({
        eventType: "published",
        title,
        startDate: dateRange[0],
        endDate: dateRange[1],
        isMunicipality:
          organizer === "Municipalities"
            ? true
            : organizer === "Organizers"
            ? false
            : undefined,
        userUid:
          organizer !== "Municipalities" && organizer !== "users"
            ? organizers?.find((user: any) => user.name === organizer)?.uid
            : undefined,
        madeByOrganizer: organizerEvents || undefined,
      }),
    },
    {
      hookName: "draftEventsCount",
      hook: useQueryEventsCount({
        eventType: "draft",
        title,
        startDate: dateRange[0],
        endDate: dateRange[1],
        isMunicipality:
          organizer === "Municipalities"
            ? true
            : organizer === "Organizers"
            ? false
            : undefined,
        userUid:
          organizer !== "Municipalities" && organizer !== "users"
            ? organizers?.find((user: any) => user.name === organizer)?.uid
            : undefined,
        madeByOrganizer: organizerEvents || undefined,
      }),
    },
    {
      hookName: "archievedEventsCount",
      hook: useQueryEventsCount({
        eventType: "archieved",
        title,
        startDate: dateRange[0],
        endDate: dateRange[1],
        isMunicipality:
          organizer === "Municipalities"
            ? true
            : organizer === "Organizers"
            ? false
            : undefined,
        userUid:
          organizer !== "Municipalities" && organizer !== "users"
            ? organizers?.find((user: any) => user.name === organizer)?.uid
            : undefined,
        madeByOrganizer: organizerEvents || undefined,
      }),
    },
    { hookName: "currentUser", hook: useQueryCurrentUser() },
  ]);

  // If translations are not ready yet, show a minimal loader
  if (!translationsReady) {
    return (
      <Center style={{ width: "100%", height: "100vh" }}>
        <Text>Loading translations...</Text>
      </Center>
    );
  }

  if (isError || isErrorUsers) {
    return (
      <div>
        {t("errors.something_went_wrong")}
        {isError ? error?.message : errorUsers?.message}
      </div>
    );
  }
  if (isLoading || isLoadingUsers) {
    return null;
  }

  const changePage = (page: number) => {
    setSearchParams({ page: page.toString() });
  };

  const goToEvent = (uid: string) => {
    navigate(`/events/${uid}`);
  };

  const goToCreateEvent = () => {
    navigate(`/events/create`);
  };

  const onDeleteEvent = (uid: string) => {
    deleteEvent.mutate({ uid });
  };

  const copyEvent = (uid: string) => {
    navigate(`/events/${uid}/duplicate`);
  };

  const updateTitle = (title: string) => {
    setTitle(title);
    if (title === "") {
      searchParams.delete("title");
      setSearchParams(searchParams);
    } else {
      searchParams.set("title", title);
      setSearchParams(searchParams);
    }
  };

  const updateType = (type?: number) => {
    setType(type);
    if (!type) {
      searchParams.delete("type");
      setSearchParams(searchParams);
    } else {
      searchParams.set("type", type.toString());
      setSearchParams(searchParams);
    }
  };

  const updateDateRange = (dateRange?: [Date | null, Date | null]) => {
    if (!dateRange) {
      searchParams.delete("start_date");
      searchParams.delete("end_date");
      setSearchParams(searchParams);
    } else {
      const [startDate, endDate] = dateRange;
      if (startDate) {
        searchParams.set("start_date", startDate.toISOString());
      } else {
        searchParams.delete("start_date");
      }
      if (endDate) {
        searchParams.set("end_date", endDate.toISOString());
      } else {
        searchParams.delete("end_date");
      }
      setSearchParams(searchParams);
    }
    setDateRange(dateRange || [null, null]);
  };

  const updateOrganizer = (organizer?: string) => {
    setOrganizer(organizer);
    if (!organizer) {
      searchParams.delete("organizer");
      setSearchParams(searchParams);
    } else {
      searchParams.set("organizer", organizer);
      setSearchParams(searchParams);
    }
  };

  const updateOrganizerEvents = (checked: boolean) => {
    setOrganizerEvents(checked);
    if (!checked) {
      searchParams.delete("organizer_events");
    } else {
      searchParams.set("organizer_events", "true");
    }
    setSearchParams(searchParams);
  };

  const updateTab = (tab: string | null) => {
    setActiveTab(tab as PublishedState);
    searchParams.set("published_state", tab as PublishedState);
    setSearchParams(searchParams);
  };

  const runBatch = async () => {
    setBatchRunning(true);
    const batchEventsClone = [...batchEvents];

    if (batchAction === "Delete") {
      deleteEvents.mutateAsync({
        uids: batchEventsClone.map((pos) => events.events[pos].uid),
      });
    }
    setBatchEvents([]);
    setBatchRunning(false);
  };

  try {
    return (
      <>
        <Stack>
          <Group justify="space-between">
            <Group>
              {currentUser.user.organizer_id === null && (
                <>
                  <EventsSearchFilter
                    title={title}
                    type={type}
                    dateRange={dateRange}
                    organizer={organizer}
                    organizerEvents={organizerEvents}
                    organizers={organizers!.map((user: any) => ({
                      uid: user.uid,
                      name: user.name,
                    }))}
                    onUpdateTitle={updateTitle}
                    onUpdateType={updateType}
                    onUpdateDateRange={updateDateRange}
                    onUpdateOrganizer={updateOrganizer}
                    onUpdateOrganizerEvents={updateOrganizerEvents}
                  />
                  {batchAction && (
                    <Button
                      onClick={() => {
                        setApproveBatch(true);
                      }}
                    >
                      {t("common.submit")}
                    </Button>
                  )}
                </>
              )}
            </Group>
            <Button onClick={goToCreateEvent}>{t("common.create")}</Button>
          </Group>
          <Tabs value={activeTab} onChange={updateTab}>
            <Tabs.List>
              <Tabs.Tab value="published">
                {t("events.published")} (
                <span style={{ opacity: publishedEventsCount.isFetching ? 0.4 : 1, transition: 'opacity 0.15s' }}>
                  {publishedEventsCount.eventsCount.count || 0}
                </span>
                )
              </Tabs.Tab>
              <Tabs.Tab value="draft">
                {t("events.draft")} (
                <span style={{ opacity: draftEventsCount.isFetching ? 0.4 : 1, transition: 'opacity 0.15s' }}>
                  {draftEventsCount.eventsCount.count || 0}
                </span>
                )
              </Tabs.Tab>
              <Tabs.Tab value="archived">
                {t("events.archived")} (
                <span style={{ opacity: archievedEventsCount.isFetching ? 0.4 : 1, transition: 'opacity 0.15s' }}>
                  {archievedEventsCount.eventsCount.count || 0}
                </span>
                )
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>
          <EventsList
            events={events.events || []}
            batchAction={batchAction}
            onEventChosen={goToEvent}
            onEventDelete={onDeleteEvent}
            onEventCopy={copyEvent}
            batchEvents={batchEvents}
            onBatchEventChosen={setBatchEvents}
            organizerView={currentUser.user.organizer_id !== null}
          />
          <Center mt="xl">
            <Pagination
              mr="xl"
              value={page}
              total={Math.ceil(events.count! / perPage)}
              onChange={changePage}
            />
          </Center>
          <LoadingOverlay
            visible={batchRunning}
            zIndex={1000}
            overlayProps={{ radius: "sm", blur: 2 }}
          />
        </Stack>
        <YesNoDialog
          opened={approveBatch}
          onClose={() => {
            setApproveBatch(false);
          }}
          title={t("events.approve_batch")}
          question={t("events.approve_batch_question")}
          onYes={runBatch}
        />
      </>
    );
  } catch (e) {
    console.error("Error rendering Events component:", e);
    return (
      <Center style={{ width: "100%", height: "100vh" }}>
        <Text>
          Something went wrong loading the events page. Please try refreshing.
        </Text>
      </Center>
    );
  }
};
